'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import { Copy01Icon, Tick01Icon } from '@terra/ui-icons';
import { IconButton } from '../../../../components/icon-button';
import { MetaDefault, MonoSmall } from '../../../../components/typography';
import { MarkdownPreview } from '../../../markdown-preview';
import type { TraceToolErrorDisplay } from '../utils/resolve-trace-tool-error';
import { TRACE_INLINE_SCROLL_MAX_HEIGHT } from '../utils/trace-inline-layout';

type TraceInlineErrorBlockProps = {
  display: TraceToolErrorDisplay;
};

const COPIED_FEEDBACK_MS = 2000;

function TraceInlineErrorContent({
  display,
}: {
  display: TraceToolErrorDisplay;
}) {
  const detail = display.detail ?? display.summary ?? '';

  switch (display.kind) {
    case 'markdown':
      return <MarkdownPreview source={detail} fontSize={12} compact />;
    case 'json':
    case 'long':
      return (
        <MonoSmall
          as="pre"
          display="block"
          color="fg.muted"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {detail}
        </MonoSmall>
      );
    case 'short':
      return (
        <MetaDefault
          as="p"
          color="fg.error"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {detail}
        </MetaDefault>
      );
  }
}

export function TraceInlineErrorBlock({ display }: TraceInlineErrorBlockProps) {
  const t = useTranslations('designSystem.chat.traceEntity');
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => () => clearTimeout(copiedTimeoutRef.current), []);

  const copyText = display.detail ?? display.summary;

  if (!copyText) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setIsCopied(true);
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(
        () => setIsCopied(false),
        COPIED_FEEDBACK_MS,
      );
    } catch (error: unknown) {
      console.error('Failed to copy trace tool error to clipboard', error);
    }
  };

  const isScrollable = display.kind !== 'short';

  return (
    <HStack
      data-trace-inline-error=""
      data-error-kind={display.kind}
      alignItems="flex-start"
      gap={2}
      minW={0}
    >
      <Box
        flex={1}
        minW={0}
        maxH={isScrollable ? TRACE_INLINE_SCROLL_MAX_HEIGHT : undefined}
        overflowY={isScrollable ? 'auto' : undefined}
      >
        <TraceInlineErrorContent display={display} />
      </Box>
      <IconButton
        size="xs"
        variant="ghost"
        flexShrink={0}
        color="fg.subtle"
        aria-label={isCopied ? t('errorCopied') : t('copyError')}
        stopPropagation
        onClick={() => void handleCopy()}
      >
        {isCopied ? <Tick01Icon size={12} /> : <Copy01Icon size={12} />}
      </IconButton>
    </HStack>
  );
}
